import { Controller } from "@hotwired/stimulus"
import * as pdfjsLib from "pdfjs-dist"

// Worker is vendored to /public by `yarn build` (see package.json) and served as-is.
pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdfjs/pdf.worker.min.mjs"

// Renders the source statement PDF (one canvas per page) and, on demand, LOCATES a transaction
// line: it searches that page's text for the amount/description and highlights the match in place
// (option B). When the text can't be found — a scanned/image-only PDF with no text, or no match —
// it scrolls to the page and returns false, and the caller flashes the extracted ledger row
// instead (option A). Scoped to the transactions card, so ledger rows call #locate directly via
// Stimulus action params.
export default class extends Controller {
  static values = { url: String }
  static targets = ["pages", "status"]

  async connect() {
    this.pages = [] // [{ wrap, overlay, items: [{str, rect}] }]
    this.timer = null
    if (!this.urlValue) return
    try {
      await this.load()
    } catch (e) {
      console.error("[pdf-viewer]", e)
      if (this.hasStatusTarget) this.statusTarget.textContent = "Couldn't render the statement here — use “Open ↗”."
    }
  }

  disconnect() { if (this.timer) clearTimeout(this.timer) }

  async load() {
    const pdf = await pdfjsLib.getDocument({ url: this.urlValue, isEvalSupported: false }).promise
    const width = Math.max(320, this.pagesTarget.clientWidth - 16)

    for (let n = 1; n <= pdf.numPages; n++) {
      const page = await pdf.getPage(n)
      const scale = width / page.getViewport({ scale: 1 }).width
      const viewport = page.getViewport({ scale })

      const wrap = document.createElement("div")
      wrap.className = "relative mx-auto mb-2 shadow-sm"
      wrap.style.width = `${viewport.width}px`
      wrap.style.height = `${viewport.height}px`

      const canvas = document.createElement("canvas")
      canvas.width = viewport.width
      canvas.height = viewport.height
      canvas.className = "block"
      wrap.appendChild(canvas)

      const overlay = document.createElement("div")
      overlay.className = "pointer-events-none absolute inset-0"
      wrap.appendChild(overlay)
      this.pagesTarget.appendChild(wrap)

      await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise
      const text = await page.getTextContent()
      const items = text.items
        .filter((it) => it.str && it.str.trim())
        .map((it) => ({ str: it.str, rect: this.itemRect(it, viewport) }))
      this.pages.push({ wrap, overlay, items })
    }
    if (this.hasStatusTarget) this.statusTarget.remove()
  }

  // Device-space rectangle for a text item, from its transform composed with the viewport.
  itemRect(item, viewport) {
    const t = pdfjsLib.Util.transform(viewport.transform, item.transform)
    const h = Math.hypot(t[2], t[3]) || 10
    const w = (item.width || 0) * viewport.scale
    return { x: t[4], y: t[5] - h, w: Math.max(w, 6), h }
  }

  // Action params: page (Number), amount (String|Number), desc (String), row (String, e.g. "txn-3").
  locate(event) {
    const { page, amount, desc, row } = event.params
    this.clear()
    const hit = this.highlight(page, amount, desc)
    if (!hit) this.scrollToPage(page) // A: at least land on the right page
    if (row) this.flashRow(row)       // always draw the eye to our parsed row too
  }

  highlight(page, amount, desc) {
    const tokens = this.tokens(amount, desc)
    if (!tokens.length) return false
    // Prefer the hinted page, then fall back to scanning all pages.
    const order = []
    const hinted = this.pages[(page || 0) - 1]
    if (hinted) order.push(hinted)
    for (const p of this.pages) if (p !== hinted) order.push(p)

    for (const p of order) {
      const item = p.items.find((it) => tokens.some((tok) => it.str.includes(tok)))
      if (item) { this.draw(p, item.rect); return true }
    }
    return false
  }

  tokens(amount, desc) {
    const out = []
    const n = Math.abs(parseFloat(amount))
    if (!Number.isNaN(n) && n > 0) {
      out.push(n.toFixed(2))
      out.push(n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }))
    }
    const d = String(desc || "").trim()
    if (d.length >= 4) out.push(d.slice(0, 18))
    return out
  }

  draw(p, rect) {
    const el = document.createElement("div")
    el.className = "absolute rounded-sm bg-warning/40 ring-2 ring-warning"
    el.style.left = `${rect.x - 2}px`
    el.style.top = `${rect.y - 2}px`
    el.style.width = `${rect.w + 4}px`
    el.style.height = `${rect.h + 4}px`
    p.overlay.appendChild(el)
    el.scrollIntoView({ block: "center", behavior: "smooth" })
    this.timer = setTimeout(() => this.clear(), 5000)
  }

  clear() {
    this.pages.forEach((p) => (p.overlay.innerHTML = ""))
    if (this.timer) { clearTimeout(this.timer); this.timer = null }
  }

  scrollToPage(page) {
    const p = this.pages[(page || 1) - 1]
    if (p) p.wrap.scrollIntoView({ block: "start", behavior: "smooth" })
  }

  flashRow(row) {
    const el = document.getElementById(row)
    if (!el) return
    el.classList.add("ring-2", "ring-warning")
    el.scrollIntoView({ block: "center", behavior: "smooth" })
    setTimeout(() => el.classList.remove("ring-2", "ring-warning"), 2500)
  }
}
